import { BREAKPOINTS } from "orcs-design-system";

/** Usable middle-column floor — `appScale.sidebarMaxWidth`. */
export const MAIN_CONTENT_MIN_PX = 360;

/** `appScale.sideNavSize` — collapsed icon rail. */
export const SIDENAV_RAIL_PX = 48;

/** `SIDENAV_WIDTHS.EXPANDED` — labelled rail when keep-expanded is on. */
export const SIDENAV_ITEMS_EXPANDED_PX = 200;

/** `EXPANDED_SIZE.normal` — typical Dashboards / Teams panel. */
export const SIDENAV_PANEL_MIN_PX = 280;

export const CHAT_PANE_MIN_PX = 280;
export const CHAT_PANE_MAX_PX = 800;
export const CHAT_PANE_DEFAULT_PX = 400;

export const SIDENAV_SMALL_SCREEN_PX = BREAKPOINTS.SMALL_SCREEN;

type PaneLayoutInput = {
  windowWidth: number;
  preferredChatWidth: number;
  isChatOpen: boolean;
};

export type PaneLayout = {
  isSmallScreen: boolean;
  smallScreenBreakpoint: number;
  maxChatWidth: number;
  chatWidth: number;
};

const occupiedChat = (isChatOpen: boolean, preferredChatWidth: number) =>
  isChatOpen
    ? Math.min(CHAT_PANE_MAX_PX, Math.max(CHAT_PANE_MIN_PX, preferredChatWidth))
    : 0;

const DESKTOP_NAV_WIDTH_PX = SIDENAV_ITEMS_EXPANDED_PX + SIDENAV_PANEL_MIN_PX;

/**
 * Ask TeamForm is a sibling of the page, so a 900px *window* breakpoint
 * collapses the Dashboards panel too late. Compact when the window itself
 * is small, or when desktop nav + chat would leave the middle column
 * below MAIN_CONTENT_MIN_PX. Then cap the chat pane to that same floor.
 */
export const getPaneLayout = ({
  windowWidth,
  preferredChatWidth,
  isChatOpen,
}: PaneLayoutInput): PaneLayout => {
  const preferredOccupied = occupiedChat(isChatOpen, preferredChatWidth);
  const smallScreenBreakpoint = Math.max(
    SIDENAV_SMALL_SCREEN_PX,
    preferredOccupied + DESKTOP_NAV_WIDTH_PX + MAIN_CONTENT_MIN_PX
  );
  const isSmallScreen = windowWidth <= smallScreenBreakpoint;
  const navWidth = isSmallScreen ? 0 : DESKTOP_NAV_WIDTH_PX;
  const availableForChat = Math.max(
    0,
    windowWidth - navWidth - MAIN_CONTENT_MIN_PX
  );
  const maxChatWidth = isChatOpen
    ? Math.min(CHAT_PANE_MAX_PX, availableForChat)
    : CHAT_PANE_MAX_PX;
  const chatWidth = isChatOpen
    ? Math.min(maxChatWidth, Math.max(CHAT_PANE_MIN_PX, preferredChatWidth))
    : 0;

  return {
    isSmallScreen,
    smallScreenBreakpoint,
    maxChatWidth,
    chatWidth,
  };
};

type DragPreferredInput = {
  startPreferred: number;
  startDisplayed: number;
  /** Positive grows the right-hand pane (drag left). */
  delta: number;
};

/**
 * Map a drag to the persisted preferred width. Viewport caps apply only to
 * the displayed pane — growing while capped must not overwrite a wider save.
 * Shrinking while capped starts from the visible edge so the handle follows.
 */
export const preferredWidthFromDrag = ({
  startPreferred,
  startDisplayed,
  delta,
}: DragPreferredInput): number => {
  const nextDisplayed = startDisplayed + delta;
  const shrinkingFromCap =
    startDisplayed < startPreferred && nextDisplayed < startDisplayed;
  const next = shrinkingFromCap ? nextDisplayed : startPreferred + delta;
  return Math.min(CHAT_PANE_MAX_PX, Math.max(CHAT_PANE_MIN_PX, next));
};
